import { useState, useCallback } from 'react';

/**
 * Hook that replaces window.confirm() with a MUI-based ConfirmDialog.
 *
 * Usage:
 *   const { confirmDialogProps, confirm } = useConfirmDialog();
 *
 *   const handleDelete = async (id) => {
 *     const confirmed = await confirm({
 *       title: 'Delete Item',
 *       message: 'Are you sure?',
 *       confirmText: 'Delete',
 *       severity: 'danger',   // 'danger' | 'warning' | 'info'
 *     });
 *     if (confirmed) {
 *       await deleteItem(id);
 *     }
 *   };
 *
 *   // In JSX:
 *   <ConfirmDialog {...confirmDialogProps} />
 *
 * Free-text variant (replaces window.prompt). Pass `input`; on confirm the
 * promise resolves to an OBJECT `{ value }` instead of `true`, so an empty
 * string is still a successful confirm rather than a falsy "cancelled":
 *
 *   const res = await confirm({
 *     title: 'Cancel dispatch',
 *     input: { label: 'Reason', multiline: true },
 *   });
 *   if (!res) return;            // cancelled / backdrop
 *   await doIt(res.value);       // '' is allowed
 */
export default function useConfirmDialog() {
  const [state, setState] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    severity: 'danger',
    input: null,
    inputValue: '',
    resolve: null,
  });

  const confirm = useCallback(({
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    severity = 'danger',
    input = null,
  } = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title,
        message,
        confirmText,
        cancelText,
        severity,
        input,
        inputValue: input?.initial ?? '',
        resolve,
      });
    });
  }, []);

  const setInputValue = useCallback((inputValue) => {
    setState(s => ({ ...s, inputValue }));
  }, []);

  const handleConfirm = useCallback(() => {
    // Input mode resolves { value } so that an empty string still reads as a
    // confirm; plain mode keeps resolving `true` for every existing caller.
    state.resolve?.(state.input ? { value: state.inputValue } : true);
    setState(s => ({ ...s, open: false, resolve: null }));
  }, [state.resolve, state.input, state.inputValue]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState(s => ({ ...s, open: false, resolve: null }));
  }, [state.resolve]);

  const confirmDialogProps = {
    open: state.open,
    title: state.title,
    message: state.message,
    confirmText: state.confirmText,
    cancelText: state.cancelText,
    severity: state.severity,
    input: state.input,
    inputValue: state.inputValue,
    onInputChange: setInputValue,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return { confirmDialogProps, confirm };
}
